import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { selectedOfficeService } from '~/domain/SelectedOffice';
import { apiMethodsConst } from '~/services/methods';
import { type IToOfficeMissingInfoPatchesArgs } from '../adapters/OfficeMissingInfo';
import { OfficeMissingInfoService } from '../services/OfficeMissingInfoService';
import { officeMissingInfoKeys } from './useOfficeCredentials';

interface IUseSaveOfficeMissingInfoOptions {
  /**
   * Whether `officeId` is the store's selected office (the default). A parent that picked a child inside the
   * shift form (GOTU-14829) saves for the child while the parent stays selected: the re-read then refreshes
   * the modal's own office query and never dispatches the child into `selectedChildOffice`.
   */
  isSelectedOffice?: boolean;
}

/**
 * Saves the modal's answers to `officeId`. Whether the save succeeds or fails part-way, the office and its
 * credentials are re-read before the mutation settles, so the missing fields recompute from what was
 * actually stored.
 */
export const useSaveOfficeMissingInfo = (
  officeId?: string | null,
  { isSelectedOffice = true }: IUseSaveOfficeMissingInfoOptions = {},
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const reloadOffice = async (): Promise<void> => {
    if (!officeId) return;

    const [credentials, office] = await Promise.allSettled([
      queryClient.fetchQuery({
        queryKey: officeMissingInfoKeys.credentials(officeId),
        queryFn: () => OfficeMissingInfoService.getCredentials(officeId),
      }),
      isSelectedOffice
        ? selectedOfficeService.getSelectedOffice({ officeId })
        : queryClient.invalidateQueries({ queryKey: officeMissingInfoKeys.office(officeId) }),
    ]);

    // Keep Redux in step for the screens that still read it (Account Information) — for the selected
    // office only; a child picked in the form is not the selected office and must not become it.
    if (credentials.status === 'fulfilled' && isSelectedOffice) {
      dispatch({ type: apiMethodsConst.USER_CREDENTIALS_REQUEST, payload: credentials.value });
    }
    if (office.status === 'fulfilled' && isSelectedOffice && office.value) {
      dispatch({ type: 'SET_SELECTED_OFFICE', payload: office.value });
    }
  };

  return useMutation<void, unknown, IToOfficeMissingInfoPatchesArgs>({
    mutationFn: async (input) => {
      try {
        await OfficeMissingInfoService.save(input, officeId);
      } finally {
        await reloadOffice();
      }
    },
  });
};
